/**
 * Table State Management Hook
 *
 * Manages sorting, column filters, and integrates with storage hooks.
 */
import {
  SortingState,
  ColumnFiltersState,
  ColumnPinningState,
  ColumnOrderState,
  RowPinningState,
} from "@tanstack/react-table";
import { useMemo, useState, useEffect } from "react";
import { ActiveSortConfig } from "@/shared/hooks/useSorting";
import { useTableStateStorage } from "./useStorageHooks";
import type { TanStackColumn } from "../types";
import type { RowData } from "@tanstack/react-table";

export type UseTableStateParams<T extends RowData> = {
  storageKey?: string
  initialColumnPinning?: ColumnPinningState
  initialColumnOrder?: ColumnOrderState
  initialRowPinning?: RowPinningState
  columns: TanStackColumn<T>[]
  activeSorts?: ActiveSortConfig[]
};

export const useTableState = <T extends RowData>({
  storageKey,
  initialColumnPinning,
  initialColumnOrder,
  initialRowPinning,
  columns,
  activeSorts,
}: UseTableStateParams<T>) => {
  const formattedSorting: SortingState | undefined = useMemo(
    () => activeSorts
      ?.filter((sort) => Boolean(sort?.id) && typeof sort?.value === "string")
      .map((sort) => {
        const [, direction] = sort.value.split(" ");
        return {
          id: sort.id,
          desc: direction === "desc",
        };
      }),
    [activeSorts],
  );

  const [sorting, setSorting] = useState<SortingState>(formattedSorting ?? []);
  useEffect(() => setSorting(formattedSorting ?? []), [formattedSorting]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const storageHooks = useTableStateStorage({
    storageKey,
    initialColumnPinning,
    initialColumnOrder,
    initialRowPinning,
    columns,
  });

  return {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    ...storageHooks,
  };
};
